import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Warning, QrCode, ArrowLeft } from "@phosphor-icons/react";
import axios from "axios";
import { Button } from "@/components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ValidateTicket = () => {
  const { ticketCode } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ticketCode) validate();
  }, [ticketCode]);

  const validate = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/validate/${ticketCode}`);
      setResult(res.data);
    } catch {
      setResult({ valid: false, message: "Erro ao validar ingresso" });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <QrCode size={64} weight="duotone" className="text-[#FF5A26] mx-auto mb-4 animate-pulse" />
          <p className="text-lg text-[#115E59] font-bold">Validando ingresso...</p>
        </div>
      </div>
    );
  }

  const isValid = result?.valid;
  const ticket = result?.ticket;

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-4" data-testid="validate-page">
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-3xl p-10 shadow-lg border border-[#E4DFD3] max-w-md w-full text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="mb-6"
        >
          {isValid ? (
            <CheckCircle size={80} weight="fill" className="text-[#10B981] mx-auto" />
          ) : result?.message?.includes("já utilizado") ? (
            <Warning size={80} weight="fill" className="text-yellow-500 mx-auto" />
          ) : (
            <XCircle size={80} weight="fill" className="text-red-500 mx-auto" />
          )}
        </motion.div>

        <h1
          className={`text-3xl font-black mb-2 ${
            isValid ? "text-[#10B981]" : "text-red-500"
          }`}
          data-testid="validate-status"
        >
          {isValid ? "VÁLIDO" : "INVÁLIDO"}
        </h1>
        <p className="text-[#115E59] text-lg mb-6">{result?.message}</p>

        {ticket && (
          <div className="bg-[#F4F1EA] rounded-xl p-5 text-left space-y-2 text-sm mb-6">
            <div className="flex justify-between">
              <span className="text-[#115E59]">Código:</span>
              <span className="font-bold text-[#042F2E] font-mono">{ticket.ticket_code}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#115E59]">Item:</span>
              <span className="font-bold text-[#042F2E]">{ticket.item_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[#115E59]">Cliente:</span>
              <span className="font-bold text-[#042F2E]">{ticket.payer_name}</span>
            </div>
            {ticket.scheduled_date && (
              <div className="flex justify-between">
                <span className="text-[#115E59]">Agendado:</span>
                <span className="font-bold text-[#042F2E]">
                  {new Date(ticket.scheduled_date + "T12:00:00").toLocaleDateString("pt-BR")}
                  {ticket.scheduled_time ? ` às ${ticket.scheduled_time}` : ""}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-[#115E59]">Valor:</span>
              <span className="font-bold text-[#042F2E]">
                {ticket.amount === 0 ? "GRÁTIS" : `R$ ${ticket.amount?.toFixed(2)}`}
              </span>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <Button
            onClick={() => navigate("/")}
            variant="outline"
            className="flex-1 rounded-full border-[#E4DFD3]"
            data-testid="validate-home"
          >
            <ArrowLeft size={18} className="mr-2" /> Início
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default ValidateTicket;
